import axios from 'axios'
import { i18n } from '../../../plugins/i18n'
import cloneDeep from 'lodash.clonedeep'

import {
  setFormValues,
  setFilterValues,
  toggleFilterValues,
} from '../../../modules/mutations.js'
import { castAttributesForCamelCase } from '../../../modules/string_converter'
import { toBase64Url } from '../../../modules/convertUrlToBase64'
import { getMovieTime, getMovieSize } from '../../../modules/getMovieInfo'

export const companiesStoreSnsPosts = {
  namespaced: true,
  state: {
    postLoading: false,
    countLoading: false,
    mediaLoading: false,
    previewLoading: false,
    formValues: {
      category: [],
    },
    post: {
      id: null,
      postSnsType: '',
      title: '',
      text: '',
      status: '',
      publishedAt: new Date,
      images: [],
      movie: null,
      approverName: '',
      latestApplicantName: '',
      facebookPages: [],
      instagramBusinessAccounts: [],
      twitterUserProfiles: [],
      accounts: [],
      comments: []
    },
    reservationDateTime: null,
    posts: [],
    postCounts: {},
    meta: {},
    approvalInitMeta: {},
    selectedPostSnsType: '',
    selectedPostStatusType: 'POST',
    selectedApprovalStatusType: 'all',
    submitActionModalType: 'post',
    approvalRequestNotes: '',
    postTiming: 'reserve'
  },
  getters: {
    postLoading: (state) => {
      return state.postLoading
    },
    countLoading: (state) => {
      return state.countLoading
    },
    mediaLoading: (state) => {
      return state.mediaLoading
    },
    post: (state) => {
      const clonePost = cloneDeep(state.post)
      clonePost.images.sort((a, b) => a.index - b.index)
      return clonePost
    },
    posts: (state) => {
      return state.posts
    },
    postCounts: (state) => {
      return state.postCounts
    },
    formValues: (state) => {
      return state.formValues
    },
    meta: (state) => {
      return state.meta
    },
    filteredCount: (state) => {
      return state.meta.totalCount
    },
    approvalInitMeta: (state) => {
      return state.approvalInitMeta
    },
    selectedPostStatusType: (state) => {
      return state.selectedPostStatusType
    },
    selectedApprovalStatusType: (state) => {
      return state.selectedApprovalStatusType
    },
    approvalRequestNotes: (state) => {
      return state.approvalRequestNotes
    },
    reservationDateTime: (state) => {
      return state.reservationDateTime
    },
    previewLoading: (state) => {
      return state.previewLoading
    },
    submitActionModalType: (state) => {
      return state.submitActionModalType
    },
    postAccounts: (state) => {
      return state.post.facebookPages.concat(state.post.instagramBusinessAccounts).concat(state.post.twitterUserProfiles)
    },
    postStatusTypes: () => {
      return {
        POST: i18n.t('models.sns.postStatusType.post'),
        RESERVING: i18n.t('models.sns.postStatusType.reservingPost'),
        IN_PROGRESS: i18n.t('models.sns.postStatusType.inProgress'),
        DRAFT: i18n.t('models.sns.postStatusType.draft')
      }
    },
    postSnsTypes: () => {
      return {
        MULTI: i18n.t('models.sns.postSnsType.multi'),
        FACEBOOK: i18n.t('models.sns.postSnsType.facebook'),
        INSTAGRAM: i18n.t('models.sns.postSnsType.instagram'),
        TWITTER: i18n.t('models.sns.postSnsType.twitter'),
      }
    },
    selectPostSnsType: (state, getters) => {
      return getters.postSnsTypes[state.post.postSnsType]
    },
    selectedPostSnsType: (state) => {
      return state.selectedPostSnsType
    },
    postTiming: (state) => {
      return state.postTiming
    }
  },
  mutations: {
    setFormValues(state, payload) {
      setFormValues(state, payload)
    },
    setFilterValues(state, payload) {
      setFilterValues(state, payload)
    },
    toggleFilterValues(state, payload) {
      toggleFilterValues(state, payload)
    },
    prepareConnectPost(state) {
      state.postLoading = true
    },
    stopLoadingPost(state) {
      state.postLoading = false
      state.previewLoading = false
    },
    prepareConnectCount(state) {
      state.countLoading = true
    },
    stopLoadingCount(state) {
      state.countLoading = false
    },
    prepareConnectMedia(state) {
      state.mediaLoading = true
    },
    stopLoadingMedia(state) {
      state.mediaLoading = false
    },
    prepareConnectPreview(state) {
      state.previewLoading = true
    },
    updateSelectedPostStatusType(state, type) {
      state.selectedPostStatusType = type
    },
    updatePostSnsType(state, postSnsType) {
      state.post.postSnsType = postSnsType
    },
    updateSelectedPostSnsType(state, postSnsType) {
      state.selectedPostSnsType = postSnsType
    },
    updateImages(state, image) {
      image.id = state.post.images.length
      state.post.images.push(image)
    },
    updateMovie(state, movie) {
      state.post.movie = movie
    },
    resetPost(state) {
      state.post.categoryId = null
      state.post.title = ''
      state.post.text = ''
      state.post.publishedAt = new Date
      state.post.images = []
      state.post.movie = null
      state.post.approverName = ''
      state.post.latestApplicantName=''
      state.post.facebookPages = []
      state.post.instagramBusinessAccounts = []
      state.post.twitterUserProfiles = []
      state.post.comments = []
    },
    resetPosts(state) {
      state.posts = []
    },
    updatePosts(state, data) {
      state.posts = data.map(v => {
        const result = castAttributesForCamelCase(v.attributes)
        result.id = Number(v.id)
        if(!v.attributes.images) result.images = []
        return result
      })
    },
    updateSnsPostBasicInfo(state, { postId, title, category }) {
      const targetPost = state.posts.find(p => p.id === postId)
      targetPost.category = category
      targetPost.note.title = title
    },
    updatePostCounts(state, data) {
      const counts = castAttributesForCamelCase(data)
      state.postCounts = {
        post: counts.publishedPostsCount,
        draft: counts.draftedPostsCount,
        reserving: counts.reservingPostsCount,
        inProgress: counts.inProgressPostsCount
      }
    },
    updateSubmitActionModalType(state, submitActionModalType) {
      state.submitActionModalType = submitActionModalType
    },
    setMeta(state, meta) {
      state.meta = castAttributesForCamelCase(meta)
    },
    setApprovalInitMeta(state, meta) {
      state.approvalInitMeta = castAttributesForCamelCase(meta)
    },
    updateSelectedApprovalStatusType(state, type) {
      state.selectedApprovalStatusType = type
    },
    setPost(state, { data, isCopied }) {
      const post = data.attributes

      if (!isCopied) {
        state.post.id = data.id
        state.post.publishedAt = post.published_at ? new Date(post.published_at) : null
        state.reservationDateTime = post.note.scheduled_at ? new Date(post.note.scheduled_at) : null
      }
      if (post.category.id) state.post.categoryId = post.category.id
      if (post.note.title) state.post.title = post.note.title
      if (post.text) state.post.text = post.text
      if (post.kind) state.post.kind = post.kind
      if (post.images) {
        for (let image of post.images) {
          toBase64Url(image.url, function (base64Url) {
            let imageObj = {
              id: image.id,
              url: image.url,
              filename: image.filename,
              content: base64Url,
              width: image.width,
              height: image.height,
              type: image.type,
              size: image.size,
              index: image.index
            }
            state.post.images.push(imageObj)
          })
        }
      }
      if (post.movie) {
        toBase64Url(post.movie.url, async function (base64Url) {
          const { width, height } = await getMovieSize(post.movie.url)
          const movieTime = await getMovieTime(post.movie.url)
          const minutes = ('00' + Math.floor(movieTime / 60)).slice(-2)
          const seconds = ('00' + Math.floor(movieTime % 60)).slice(-2)
          let movieObj = {
            url: post.movie.url,
            filename: post.movie.filename,
            content: base64Url,
            time: `${minutes}:${seconds}`,
            type: post.movie.type,
            size: post.movie.size,
            width: width,
            height: height,
          }
          state.post.movie = movieObj
        })
      }
      for (let target of post.targets) {
        switch (target.kind) {
        case 'facebook':
          target.account.type = 'facebook'
          state.post.facebookPages.push(target.account)
          break
        case 'instagram':
          target.account.type = 'instagram'
          state.post.instagramBusinessAccounts.push(target.account)
          break
        case 'twitter':
          target.account.type = 'twitter'
          state.post.twitterUserProfiles.push(target.account)
          break
        }
      }
      if (isCopied) {
        return
      }
      if (post.comments) {
        state.post.comments = post.comments.map((comment) => {
          return {
            kind: comment.kind,
            body: comment.body,
            commenterName: comment.commenter_name,
            commentedAt: comment.commented_at
          }
        })
      }
    },
    updateReservationDateTime(state, reservationDateTime) {
      state.reservationDateTime = reservationDateTime
    },
    removePostImages(state, id) {
      state.post.images = state.post.images.filter(image => {
        return image.id !== id
      })
    },
    removePostMovie(state) {
      state.post.movie = null
    },
    deselectAccount(state, payload) {
      switch(payload.type){
      case 'facebook':
        state.post.facebookPages = state.post.facebookPages.filter(account => account.id !== payload.id )
        break
      case 'instagram':
        state.post.instagramBusinessAccounts = state.post.instagramBusinessAccounts.filter(account => account.id !== payload.id )
        break
      case 'twitter':
        state.post.twitterUserProfiles = state.post.twitterUserProfiles.filter(account => account.id !== payload.id )
        break
      }
    },
    deselectAllAccounts(state) {
      state.post.facebookPages = []
      state.post.instagramBusinessAccounts = []
      state.post.twitterUserProfiles = []
    },
    updatePostAccounts(state, accounts) {
      state.post.facebookPages = []
      state.post.instagramBusinessAccounts = []
      state.post.twitterUserProfiles = []
      for (let account of accounts) {
        switch (account.type) {
        case 'facebook':
          state.post.facebookPages.push(account)
          break
        case 'instagram':
          state.post.instagramBusinessAccounts.push(account)
          break
        case 'twitter':
          state.post.twitterUserProfiles.push(account)
          break
        }
      }
    },
    updateApprovalRequestNotes(state, notes) {
      state.approvalRequestNotes = notes
    },
    updatePostTiming(state, postTiming) {
      state.postTiming = postTiming
    },
    updatePostCategoryId(state, id) {
      state.post.categoryId = id
    },
    updatePostTitle(state, title) {
      state.post.title = title
    },
    updatePostText(state, text) {
      state.post.text = text
    },
  },
  actions: {
    updateSubmitActionModalType({ commit }, submitActionModalType) {
      commit('updateSubmitActionModalType', submitActionModalType)
    },
    resetPost({ commit }) {
      commit('resetPost')
    },
    resetPosts({ commit }) {
      commit('resetPosts')
    },
    updatePreviewLoading({ commit }, value) {
      commit('updatePreviewLoading', value)
    },
    updateApprovalRequestNotes({ commit }, notes) {
      commit('updateApprovalRequestNotes', notes)
    },
    updatePostTiming({ commit }, postTiming) {
      commit('updatePostTiming', postTiming)
    },
    updateImages({ commit }, image) {
      commit('updateImages', image)
    },
    updateMovie({ commit }, movie) {
      commit('updateMovie', movie)
    },
    updateApprovalRequest({ rootState, commit }, { id, isApprove, payload }) {
      let targetEndpoint

      if (isApprove) {
        targetEndpoint = '/api/companies/' + rootState.route.params.company_uuid + '/sns/requesting_posts/' + id + '/approve'
      } else {
        targetEndpoint = '/api/companies/' + rootState.route.params.company_uuid + '/sns/requesting_posts/' + id + '/reject'
      }

      return axios
        .post(targetEndpoint, payload)
        .then(() => {
          commit('flash/updateFlash', { type: 'notice', message: i18n.t(`pages.CompaniesStoreSnsPost.index.${ isApprove ? 'approve' : 'reject' }Success`) }, { root: true })
          return true
        })
        .catch(err => {
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    setFormValues({ commit }, payload) {
      commit('setFormValues', payload)
    },
    deletePostImage({ commit }, id) {
      commit('removePostImages', id)
    },
    deletePostMovie({ commit }) {
      commit('removePostMovie')
    },
    readPosts({ commit, rootState }, payload) {
      commit('prepareConnectPost')
      return axios
        .post('/api/companies/' + rootState.route.params.company_uuid + '/sns/posts/search', payload)
        .then(res => {
          commit('updatePosts', res.data.data)
          commit('setMeta', res.data.meta)
          commit('stopLoadingPost')
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    readPostsForFilter({ commit, rootState }, payload) {
      commit('prepareConnectPost')
      return axios
        .post('/api/companies/' + rootState.route.params.company_uuid + '/sns/posts/search', payload)
        .then(res => {
          commit('setMeta', res.data.meta)
          commit('stopLoadingPost')
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    createPost({ rootState, commit }, payload) {
      commit('prepareConnectPost')
      return axios
        .post('/api/companies/' + rootState.route.params.company_uuid + '/sns/posts', payload)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    updatePost({ rootState, commit }, { payload, id }) {
      commit('prepareConnectPost')
      return axios
        .put('/api/companies/' + rootState.route.params.company_uuid + '/sns/posts/' + id, payload)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    getPost({ commit }, { companyUuid, id, isCopied = false, isPreview = false }) {
      isPreview ? commit('prepareConnectPreview') : commit('prepareConnectPost')
      commit('resetPost')
      return axios
        .get('/api/companies/' + companyUuid + '/sns/posts/' + id)
        .then(res => {
          commit('setPost', { data: res.data.data, isCopied: isCopied })
          commit('stopLoadingPost')
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    deletePost({ commit }, { companyUuid, id }) {
      commit('prepareConnectPost')
      return axios
        .delete('/api/companies/' + companyUuid + '/sns/posts/' + id)
        .then(() => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('messages.deletedDelay', { value: i18n.t('layouts.sideMenu.operation.snsPosts.name') }) }, { root: true })
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    createRequestingPost({ commit, rootState }, payload) {
      commit('prepareConnectPost')
      return axios
        .post('/api/companies/' + rootState.route.params.company_uuid + '/sns/requesting_posts', payload)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash',{ type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    createRequestingPostAfterDraft({ commit, rootState }, { payload, drafted_post_id }) {
      commit('prepareConnectPost')
      return axios
        .post('/api/companies/' + rootState.route.params.company_uuid + '/sns/requesting_posts/' + drafted_post_id + '/approval_requests', payload)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    deleteApprovalRequest({ commit, rootState }, drafted_post_id ) {
      commit('prepareConnectPost')
      return axios
        .delete('/api/companies/' + rootState.route.params.company_uuid + '/sns/requesting_posts/' + drafted_post_id + '/approval_requests')
        .then(() => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('pages.CompaniesStoreSnsPost.index.deleteRequestSuccess') }, { root: true })
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    updateRequestingPost({ commit, rootState }, { payload, id }) {
      commit('prepareConnectPost')
      return axios
        .patch('/api/companies/' + rootState.route.params.company_uuid + '/sns/requesting_posts/' + id, payload)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash',{ type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    readRequestingPosts({ commit, rootState }, payload) {
      commit('prepareConnectPost')
      return axios
        .post('/api/companies/' + rootState.route.params.company_uuid + '/sns/requesting_posts/search', payload)
        .then(res => {
          commit('updatePosts', res.data.data)
          commit('setMeta', res.data.meta)
          commit('setApprovalInitMeta', res.data.meta)
          commit('stopLoadingPost')
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    readRequestingPostsForFilter({ commit, rootState }, payload) {
      commit('prepareConnectPost')
      return axios
        .post('/api/companies/' + rootState.route.params.company_uuid + '/sns/requesting_posts/search', payload)
        .then(res => {
          commit('setMeta', res.data.meta)
          commit('stopLoadingPost')
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    getRequestingPost({ commit, rootState }, { id, isCopied = false, isPreview = false }) {
      isPreview ? commit('prepareConnectPreview') : commit('prepareConnectPost')
      commit('resetPost')
      return axios
        .get('/api/companies/' + rootState.route.params.company_uuid + '/sns/requesting_posts/' + id)
        .then(res => {
          commit('setPost', { data: res.data.data, isCopied: isCopied })
          commit('stopLoadingPost')
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    deleteRequestingPost({ commit, rootState }, id) {
      commit('prepareConnectPost')
      return axios
        .delete('/api/companies/' + rootState.route.params.company_uuid + '/sns/requesting_posts/' + id)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    createPostAfterRequesting({ commit, rootState }, { payload, id }) {
      commit('prepareConnectPost')
      return axios
        .post('/api/companies/' + rootState.route.params.company_uuid + '/sns/drafted_posts/' + id + '/posts', payload)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    deleteRequestApprovingPost({ commit, rootState }, requestingPostId) {
      commit('prepareConnectPost')
      return axios
        .delete('/api/companies/' + rootState.route.params.company_uuid + '/sns/requesting_posts/' + requestingPostId)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    readDraftedPosts({ commit, rootState }, payload) {
      commit('prepareConnectPost')
      return axios
        .get('/api/companies/' + rootState.route.params.company_uuid + '/sns/drafted_posts', { params: payload })
        .then(res => {
          commit('updatePosts', res.data.data)
          commit('setMeta', res.data.meta)
          commit('stopLoadingPost')
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    getDraftedPost({ commit, rootState }, { id, isCopied = false, isPreview = false }) {
      isPreview ? commit('prepareConnectPreview') : commit('prepareConnectPost')
      commit('resetPost')
      return axios
        .get('/api/companies/' + rootState.route.params.company_uuid + '/sns/drafted_posts/' + id)
        .then(res => {
          commit('setPost', { data: res.data.data, isCopied: isCopied })
          commit('stopLoadingPost')
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash',{ type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    createDraftedPost({ commit, rootState }, payload) {
      commit('prepareConnectPost')
      return axios
        .post('/api/companies/' + rootState.route.params.company_uuid + '/sns/drafted_posts', payload)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash',{ type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    updateDraftedPost({ commit, rootState }, { payload, id }) {
      commit('prepareConnectPost')
      return axios
        .put('/api/companies/' + rootState.route.params.company_uuid + '/sns/drafted_posts/' + id, payload)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash',{ type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    deleteDraftedPost({ commit, rootState }, draftPostId) {
      commit('prepareConnectPost')
      return axios
        .delete('/api/companies/' + rootState.route.params.company_uuid + '/sns/drafted_posts/' + draftPostId)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    createPostAfterDraft({ commit, rootState }, { payload, draftedPostId }) {
      commit('prepareConnectPost')
      return axios
        .post('/api/companies/' + rootState.route.params.company_uuid + '/sns/drafted_posts/' + draftedPostId + '/posts', payload)
        .then(() => {
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash',{ type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    updateSnsPostBasicInfo({ rootState, commit }, { postId, payload, category }) {
      commit('prepareConnectPost')
      return axios
        .put(`/api/companies/${rootState.route.params.company_uuid}/sns/posts/${postId}/basic_info`, payload)
        .then(() => {
          const title = payload.title
          commit('updateSnsPostBasicInfo', { postId, title, category })
          commit('stopLoadingPost')
          return true
        })
        .catch(err => {
          commit('stopLoadingPost')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    readPostCounts({ commit, rootState }) {
      commit('prepareConnectCount')
      return axios
        .get('/api/companies/' + rootState.route.params.company_uuid + '/sns/posts/counts')
        .then(res => {
          commit('updatePostCounts', res.data.data)
          commit('stopLoadingCount')
        })
        .catch(err => {
          commit('stopLoadingCount')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    updateSelectedApprovalStatusType({ commit }, type) {
      commit('updateSelectedApprovalStatusType', type)
    },
    updateSelectedPostStatusType({ commit }, payload) {
      commit('updateSelectedPostStatusType', payload)
    },
    updatePostSnsType({ commit }, payload) {
      commit('updatePostSnsType', payload)
    },
    updateSelectedPostSnsType({ commit }, payload) {
      commit('updateSelectedPostSnsType', payload)
    },
    updateReservationDateTime({ commit }, payload) {
      commit('updateReservationDateTime', payload)
    },
    deselectAccount({ commit }, payload) {
      commit('deselectAccount', payload)
    },
    deselectAllAccounts({ commit }) {
      commit('deselectAllAccounts')
    },
    updatePostAccounts({ commit }, accounts) {
      commit('updatePostAccounts', accounts)
    },
    updatePostTitle({ commit }, title) {
      commit('updatePostTitle', title)
    },
    updatePostCategoryId({ commit }, id) {
      commit('updatePostCategoryId', id)
    },
    updatePostText({ commit }, text) {
      commit('updatePostText', text)
    },
  }
}
